/* New Solmi landing page, ported from the solmi-landing.html design
   (shizudio/Vibabie). The stylesheet is scoped under .slv2 (see
   landing-v2.css) and all imperative behaviour lives in initLanding(),
   queried off the wrapper element, so nothing leaks into the rest of the
   SPA and everything is torn down on unmount. */
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type CSSProperties, type MouseEvent } from "react";
import { useLocation } from "wouter";
import posthog from "posthog-js";
import AuthModal from "@/components/AuthModal";
import AdGeneratorFooterLink from "@/components/AdGeneratorFooterLink";
import BestMvFooterLink from "@/components/BestMvFooterLink";
import MusicGeneratorFooterLink from "@/components/MusicGeneratorFooterLink";
import ComposerV2, { type ComposerV2Handle, type ComposerV2Mode } from "@/components/landingV2/ComposerV2";
import HeaderV2 from "@/components/landingV2/HeaderV2";
import StatsBand from "@/components/landingV2/StatsBand";
import {
  CLIPS,
  planWallColumns,
  wallCenterColumn,
  wallMetrics,
  type WallClip,
} from "@/components/landingV2/wallClips";
import type { DemoMusicVideo } from "@/data/demoMusicVideos";
import { useAuth } from "@/_core/hooks/useAuth";
import { useSEO } from "@/hooks/useSEO";
import { useTranslation } from "react-i18next";
import "@/locales/registerNewLandingI18n";
import "@/locales/registerLandingV2I18n";
import { LANDING_SAMPLE_SONGS } from "@/lib/composerSource";
import { DISCORD_INVITE_URL } from "@/const";
import { trackDiscordClick, type AuthModalTrigger } from "@/lib/dataLayer";
import {
  APP_DOWNLOAD_QR_IMAGE,
  APP_DOWNLOAD_QR_TARGET_URL,
  LANDING_ATTRIBUTION,
  createAppDownloadClickId,
  getAndroidPlayStoreUrl,
  getAppDownloadPlatform,
  getAppDownloadTrackingParams,
  getIosAppStoreUrl,
  openIosAppStoreWithFallback,
} from "@/lib/appDownloadLinks";
import { LISTED_ON_DIRECTORIES } from "@shared/listedOnDirectories";
import "./landing-v2.css";
import "./landing-v2-app.css";

const CREATE_PATH = "/ai-music-video-generator";
// Picker index → composer mode (Storyboard, Singing, Lyric, Song).
// Bump when a tile clip/poster is replaced: same path, so browsers cache the old one.
const TYPES_V = "5";
// The Song tile has no clip; its speaker plays a sample track instead.
const SONG_TILE_AUDIO = LANDING_SAMPLE_SONGS[1].audioUrl ?? "";
// Four formats. The Dance card is hidden while the dance product is in
// Beta with no recorded moves (it renders the same way Music video does and
// costs more per shot); flip SHOW_DANCE_CARD to bring it back — the composer
// mode, copy and assets stay in place. /ai-dance-video-generator still
// serves the dance product directly.
const SHOW_DANCE_CARD = false;
const PICK_MODES: ComposerV2Mode[] = SHOW_DANCE_CARD
  ? ["video", "singing", "dance", "lyrics", "song"]
  : ["video", "singing", "lyrics", "song"];
const CDN = "https://media.solmi.ai/ai-music-video/";
// The gallery popup (prompt, like, share, Remix) and the demo catalogue it
// reads are fetched when the wall first scrolls into view, not with the page.
const loadWallDemoPopup = () => import("@/components/landingV2/WallDemoPopup");
const WallDemoPopup = lazy(loadWallDemoPopup);

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700;1,9..40,400;1,9..40,500&family=DM+Mono:wght@300;400;500&display=swap";

/* A landing section that failed to set up. The page is still usable, so it
   goes to PostHog as a handled warning, not through the ErrorBoundary. */
function reportLandingSectionError(section: string, error: unknown) {
  console.error(`[LandingV2] ${section} setup failed`, error);
  try {
    posthog.captureException(error, {
      $exception_level: "warning",
      error_classification: "landing_section_failed",
      error_source: "landing_v2",
      landing_section: section,
    });
  } catch {
    // Reporting must never throw from the landing's setup.
  }
}

/* All of the page's behaviour, ported from the design's inline script.
   Queries are scoped to the .slv2 wrapper; dispose() removes every listener,
   observer and animation frame it created. */
type LandingText = {
  captions: string[];
  alts: string[];
  step: (n: number) => string;
  notPicked: string;
};
type LandingController = {
  dispose(): void;
  /** The gallery popup opened/closed: hold every other video and the wall. */
  setPopupOpen(open: boolean): void;
  /** Pick a format card, as a click on it would. */
  selectStyle(i: number): void;
};
function initLanding(
  root: HTMLElement,
  opts: {
    onPick: (i: number) => void;
    onOpenClip: (clip: WallClip) => void;
    /** The wall reached the viewport: time to fetch the popup's chunk. */
    onWallSeen: () => void;
    /** A section's setup threw; the rest of the page carries on. */
    onSectionError: (section: string, error: unknown) => void;
    text: LandingText;
  },
): LandingController {
  const $ = <T extends HTMLElement = HTMLElement>(s: string) =>
    root.querySelector(s) as T;
  const $$ = <T extends HTMLElement = HTMLElement>(s: string) =>
    Array.from(root.querySelectorAll(s)) as T[];

  const cleanups: Array<() => void> = [];
  const on = (
    t: EventTarget,
    ev: string,
    fn: EventListenerOrEventListenerObject,
    opts?: AddEventListenerOptions,
  ) => {
    t.addEventListener(ev, fn, opts);
    cleanups.push(() => t.removeEventListener(ev, fn, opts));
  };
  // The sections below only add behaviour to markup React already rendered.
  // One that throws (a stylesheet the browser never applied, a missing media
  // API) loses just its own behaviour: thrown from this effect, it used to
  // reach the app ErrorBoundary and replace "/" with the fatal error screen.
  const section = (name: string, run: () => void) => {
    try {
      run();
    } catch (error) {
      opts.onSectionError(name, error);
    }
  };

  // Set by the sections below; setPopupOpen() fans out to them.
  let popupOpen = false;
  const popupHooks: Array<(open: boolean) => void> = [];

  /* --- theme: system preference by default; an explicit choice wins --- */
  const storedTheme = (() => {
    try {
      return localStorage.getItem("solmi-theme");
    } catch {
      return null;
    }
  })();
  if (storedTheme) root.dataset.theme = storedTheme;
  const isDark = () =>
    root.dataset.theme
      ? root.dataset.theme === "dark"
      : matchMedia("(prefers-color-scheme: dark)").matches;
  on($("#theme"), "click", () => {
    const next = isDark() ? "light" : "dark";
    root.dataset.theme = next;
    try {
      localStorage.setItem("solmi-theme", next);
    } catch {}
    pushDock();
  });
  const schemeMq = matchMedia("(prefers-color-scheme: dark)");
  on(schemeMq, "change", () => {
    if (!root.dataset.theme) pushDock();
  });

  /* --- style picker --- */
  $$(".style").forEach((s) => {
    on(s, "click", (e) => {
      if ((e.target as HTMLElement).closest(".snd")) return;
      const wasOn = s.getAttribute("aria-pressed") === "true";
      $$(".style").forEach((x) => x.setAttribute("aria-pressed", "false"));
      s.setAttribute("aria-pressed", String(!wasOn));
      pushDock();
    });
  });

  /* --- sticky composer --- */
  const dock = $("#dock");
  const dockStyle = $("#dockStyle");
  const dockSwatch = $("#dockSwatch");
  const dockBg = $("#dockBg");
  const composer = $(".composer");

  let past = false;
  let atClose = false;
  const dockEarned = () =>
    innerWidth > 820 || !!root.querySelector(".style[aria-pressed=true]");
  const applyDock = () => {
    const show = past && dockEarned();
    dock.dataset.show = String(show);
    dock.dataset.fade = String(atClose);
    const live = show && !atClose;
    dock.setAttribute("aria-hidden", String(!live));
    if (!live) closePops();
  };
  const composerIo = new IntersectionObserver(
    ([e]) => {
      past = !e.isIntersecting;
      applyDock();
    },
    { rootMargin: "-40px 0px 0px 0px" },
  );
  composerIo.observe(composer);
  const closeIo = new IntersectionObserver(
    ([e]) => {
      atClose = e.isIntersecting;
      applyDock();
    },
    { threshold: 0.3 },
  );
  closeIo.observe($(".close"));
  cleanups.push(() => {
    composerIo.disconnect();
    closeIo.disconnect();
  });

  /* Format clips autoplay on fine pointers; touch plays the picked card only,
     so phones don't stream three clips for cards a few dozen pixels tall. */
  section("clips", () => {
    const cards = $$(".picker .style");
    if (!cards.length) return;
    const reduce = matchMedia("(prefers-reduced-motion:reduce)").matches;
    const fine = matchMedia("(hover:hover) and (pointer:fine)").matches;
    const start = (v: HTMLVideoElement | null) => {
      if (!v || reduce) return;
      if (v.preload === "none") {
        v.preload = "auto";
        v.load();
      }
      const p = v.play();
      if (p) p.then(() => v.classList.add("on")).catch(() => {});
    };
    const stop = (v: HTMLVideoElement | null) => {
      if (!v) return;
      v.pause();
      v.currentTime = 0;
      v.classList.remove("on");
    };
    if (fine) {
      const startAll = () =>
        cards.forEach((c) => start(c.querySelector<HTMLVideoElement>(".clip")));
      startAll();
      // Browsers pause background-tab video; resume when the tab is shown.
      on(document, "visibilitychange", () => {
        if (document.visibilityState === "visible" && !popupOpen) startAll();
      });
      // Looping clips decoding under the popup cost its video frames.
      popupHooks.push((open) => {
        if (open) cards.forEach((c) => c.querySelector<HTMLVideoElement>(".clip")?.pause());
        else startAll();
      });
    } else {
      const sync = () =>
        cards.forEach((c) => {
          const v = c.querySelector<HTMLVideoElement>(".clip");
          c.getAttribute("aria-pressed") === "true" ? start(v) : stop(v);
        });
      cards.forEach((c) => on(c, "click", () => setTimeout(sync, 0)));
      popupHooks.push((open) => {
        if (open) cards.forEach((c) => c.querySelector<HTMLVideoElement>(".clip")?.pause());
        else sync();
      });
    }
  });

  /* ---- single source of truth: the hero picker + the hero field ---- */
  const styles = $$(".style");
  function selectStyle(i: number) {
    styles.forEach((x, n) => x.setAttribute("aria-pressed", String(n === i)));
    pushDock();
  }

  const BASE_ACCENT = "#1ED760";
  let plateSrc: string | null = null;
  function setPlate(src: string) {
    if (src === plateSrc) return;
    plateSrc = src;
    dockBg.style.opacity = "0";
    requestAnimationFrame(() => {
      dockBg.style.setProperty("--art", src ? 'url("' + src + '")' : "none");
      requestAnimationFrame(() => {
        dockBg.style.opacity = "1";
      });
    });
  }
  function pushDock() {
    const i = styles.findIndex((x) => x.getAttribute("aria-pressed") === "true");
    let acc = i > -1 ? styles[i].dataset.accent || BASE_ACCENT : BASE_ACCENT;
    if (acc === "#8A8F8C" && isDark()) acc = "#B9BEBB";
    root.style.setProperty("--accent", acc);
    if (i > -1) {
      // First text node only: the descriptor line under the name stays out.
      dockStyle.textContent =
        styles[i].querySelector(".nm")?.childNodes[0]?.textContent?.trim() || "";
      dockStyle.classList.remove("empty");
      const im = styles[i].querySelector<HTMLImageElement>(".ph");
      const src = im ? im.currentSrc || im.src : "";
      dockSwatch.style.background = src ? "url(" + src + ") center/cover" : "";
      setPlate(src);
    } else {
      dockStyle.textContent = opts.text.notPicked;
      dockStyle.classList.add("empty");
      dockSwatch.style.background = "";
      setPlate("");
    }
    $$(".look-opt").forEach((b, n) =>
      b.setAttribute("aria-pressed", String(n === i)),
    );
    applyDock();
    $(".picker").classList.toggle("has-pick", i > -1);
    opts.onPick(i);
  }

  /* ---- popovers: the slot IS the control ---- */
  const wraps = { look: $("#lookWrap") };
  const closePops = (except?: HTMLElement) => {
    Object.values(wraps).forEach((w) => {
      if (w === except) return;
      w.dataset.open = "false";
      w.querySelector(".dslot")?.setAttribute("aria-expanded", "false");
    });
  };
  const togglePop = (w: HTMLElement) => {
    const open = w.dataset.open !== "true";
    closePops(w);
    w.dataset.open = String(open);
    w.querySelector(".dslot")?.setAttribute("aria-expanded", String(open));
  };
  on($("#lookBtn"), "click", (e) => {
    e.stopPropagation();
    togglePop(wraps.look);
  });
  $$(".dpop").forEach((d) => on(d, "click", (e) => e.stopPropagation()));
  on(document, "click", () => closePops());
  on(document, "keydown", (e) => {
    if ((e as KeyboardEvent).key === "Escape") closePops();
  });

  $$(".look-opt").forEach((b) => {
    on(b, "click", () => {
      selectStyle(+(b.dataset.i || 0));
      closePops();
    });
  });
  /* --- hero disk sinks on scroll --- */
  section("hero", () => {
    if (matchMedia("(prefers-reduced-motion:reduce)").matches) return;
    let queued = false;
    const fade = () => {
      queued = false;
      const t = Math.min(1, Math.max(0, scrollY / (innerHeight * 1.5)));
      root.style.setProperty("--dome-fade", String(1 - t));
      root.style.setProperty("--disk-lift", (-380 * t).toFixed(1) + "px");
      root.style.setProperty("--disk-scale", (1 - 0.3 * t).toFixed(4));
    };
    on(
      window,
      "scroll",
      () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(fade);
        }
      },
      { passive: true },
    );
    on(window, "resize", fade);
    fade();
  });

  /* --- how it works: scroll drives the visual --- */
  const steps = $$(".beat");
  const hiwStage = $("#hiwStage");
  const hiwTag = $("#hiwTag");
  const hiwCaption = $("#hiwCaption");
  const hiwShot = $<HTMLImageElement>("#hiwShot");
  // Same product shots as the current landing's workflow tour.
  const STEP_SHOTS = [
    {
      tint: "#45A85E",
      shot: "/product-tour/workflow-add-music.png",
    },
    {
      tint: "#00C4E7",
      shot: "/product-tour/workflow-music-analysis.png",
    },
    {
      tint: "#F5811A",
      shot: "/product-tour/workflow-visual-style.webp",
    },
    {
      tint: "#7B6FD6",
      shot: "/product-tour/workflow-references.webp",
    },
    {
      tint: "#D8434F",
      shot: "/product-tour/workflow-storyboard.webp",
    },
    {
      tint: "#1ED760",
      shot: "/product-tour/workflow-render.webp",
      // The finished cut itself — the stage becomes a player on this step.
      video: "/product-tour/render-timeline/paper-boats.mp4",
      poster: "/product-tour/render-timeline/paper-boats-poster.jpg",
    },
  ];
  const STEP_VISUALS = STEP_SHOTS.map((shot, i) => ({
    ...shot,
    alt: opts.text.alts[i] ?? "",
    caption: opts.text.captions[i] ?? "",
  }));
  // Four headings, six screens: each shot belongs to a step.
  const SHOT_STEP = [0, 1, 1, 2, 3, 3];
  const STEP_FIRST_SHOT = [0, 1, 3, 4];
  section("hiw", () => {
    if (!steps.length || !hiwStage) return;
    // Stacked transition: the outgoing shot slides up and shrinks while the
    // next one rises into place (reversed when scrolling back up).
    let current: HTMLImageElement = hiwShot;
    let currentIndex = 0;
    let swapToken = 0;
    let lockUntil = 0;
    const hiwPlay = $("#hiwPlay");
    const hiwVideo = $<HTMLVideoElement>("#hiwVideo");
    const stopPlayer = () => {
      if (!hiwVideo.hidden) {
        hiwVideo.pause();
        hiwVideo.hidden = true;
      }
      hiwStage.classList.remove("playing");
    };
    on(hiwPlay, "click", (e) => {
      e.stopPropagation();
      const v = STEP_VISUALS.find((x) => "video" in x && x.video);
      if (!v || !("video" in v)) return;
      if (hiwVideo.getAttribute("src") !== v.video) {
        hiwVideo.src = v.video as string;
        hiwVideo.poster = (v as { poster?: string }).poster || "";
      }
      hiwVideo.hidden = false;
      hiwStage.classList.add("playing");
      lockUntil = performance.now() + 2000;
      void hiwVideo.play().catch(() => {});
    });
    on(hiwVideo, "ended", stopPlayer);
    popupHooks.push((open) => {
      if (open) hiwVideo.pause();
    });
    const pick = (i: number) => {
      const step = SHOT_STEP[i] ?? 0;
      const hasVideo = "video" in STEP_VISUALS[i] && !!(STEP_VISUALS[i] as { video?: string }).video;
      hiwPlay.hidden = !hasVideo;
      if (!hasVideo) stopPlayer();
      steps.forEach((b, n) => b.setAttribute("aria-pressed", String(n === step)));
      const v = STEP_VISUALS[i];
      hiwStage.style.setProperty("--step", v.tint);
      hiwTag.textContent = opts.text.step(step + 1);
      hiwCaption.textContent = v.caption;
      if (current.getAttribute("src") === v.shot) {
        current.classList.add("on");
        currentIndex = i;
        return;
      }
      const token = ++swapToken;
      const dir = i > currentIndex ? "down" : "up";
      currentIndex = i;
      const next = new Image();
      next.onload = () => {
        if (token !== swapToken) return;
        const outgoing = current;
        const incoming = document.createElement("img");
        incoming.className = "shot enter";
        incoming.src = v.shot;
        incoming.alt = v.alt;
        incoming.decoding = "async";
        hiwStage.dataset.dir = dir;
        outgoing.insertAdjacentElement("afterend", incoming);
        void incoming.offsetWidth;
        incoming.classList.remove("enter");
        incoming.classList.add("on");
        outgoing.classList.remove("on");
        outgoing.classList.add("leave");
        current = incoming;
        setTimeout(() => outgoing.remove(), 700);
      };
      next.src = v.shot;
    };
    const track = $(".hiw-track");
    const BANDS = [0, 1 / 6, 2 / 6, 3 / 6, 4 / 6, 5 / 6, 1];
    // Computed height is never "auto", so ask the pin itself: below 1181px the
    // section is a plain stack and scroll must not drive the steps.
    const sticky = $(".hiw-sticky");
    const isStacked = () => !track || !sticky || getComputedStyle(sticky).position !== "sticky";
    const progress = () => {
      if (!track || isStacked()) return null;
      const r = track.getBoundingClientRect();
      const travel = r.height - innerHeight;
      if (travel <= 0) return null;
      return Math.max(0, Math.min(1, -r.top / travel));
    };
    const fromScroll = () => {
      if (performance.now() < lockUntil) return;
      const p = progress();
      if (p === null) return;
      let i = 0;
      for (let n = 0; n < 6; n++) if (p >= BANDS[n] && p < BANDS[n + 1]) i = n;
      if (p >= BANDS[6]) i = 5;
      pick(i);
    };
    let queued = false;
    on(
      window,
      "scroll",
      () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            fromScroll();
          });
        }
      },
      { passive: true },
    );
    on(window, "resize", fromScroll);
    steps.forEach((b, stepIndex) =>
      on(b, "click", () => {
        const stacked = isStacked();
        let i = STEP_FIRST_SHOT[stepIndex] ?? 0;
        // No scroll to walk the six screens when stacked, so a second tap on
        // the live step moves to its next screen (the render player is one).
        if (stacked && SHOT_STEP[currentIndex] === stepIndex && SHOT_STEP[currentIndex + 1] === stepIndex) {
          i = currentIndex + 1;
        }
        pick(i);
        if (stacked || !track) {
          // Stacked layout: the picture sits above the steps, so a tap on a
          // lower step can change a stage that has already scrolled away.
          if (hiwStage.getBoundingClientRect().top < 0) {
            hiwStage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
          return;
        }
        lockUntil = performance.now() + 900;
        const travel = track.offsetHeight - innerHeight;
        const mid = (BANDS[i] + BANDS[i + 1]) / 2;
        scrollTo({ top: track.offsetTop + travel * mid, behavior: "smooth" });
      }),
    );
    pick(0);
    fromScroll();
    // The shot swaps on scroll; a lazy <img> here never started loading in
    // some browsers, so fetch all three up front.
    STEP_VISUALS.forEach((v) => {
      const im = new Image();
      im.src = v.shot;
    });
  });

  /* --- reveals --- */
  section("reveals", () => {
    const revealed = $$("[data-reveal]");
    const canReveal =
      "IntersectionObserver" in window &&
      !matchMedia("(prefers-reduced-motion:reduce)").matches &&
      revealed.length;
    if (!canReveal) return;
    root.classList.add("js-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          en.target.classList.add("in");
          io.unobserve(en.target);
        });
      },
      { threshold: 0.14, rootMargin: "-6% 0px -12% 0px" },
    );
    revealed.forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());
    const showIfOnScreen = () =>
      revealed.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < innerHeight * 0.94 && r.bottom > 0) el.classList.add("in");
      });
    showIfOnScreen();
    on(window, "load", showIfOnScreen);
  });

  /* --- gallery wall: masonry marquee; a click opens the demo popup --- */
  section("wall", () => {
    const wall = $("#wall");
    const track = $("#wallTrack");
    if (!wall || !track) return;
    const fine = matchMedia("(hover:hover) and (pointer:fine)").matches;
    const SPEED = 34;
    let live: HTMLElement | null = null;

    const SHAPE: Record<string, number> = { "16/9": 0.5625, "9/16": 1.3333 };
    function tile(c: WallClip) {
      const el = document.createElement("button");
      el.className = "wtile";
      el.type = "button";
      el.style.setProperty("--r", (SHAPE[c.ar] || 0.5625).toFixed(4));
      el.dataset.vid = CDN + "narrative-video-" + c.u + ".mp4";
      el.dataset.u = c.u;
      el.setAttribute("aria-label", "Play " + c.title);
      if (c.z) el.style.setProperty("--zoom", String(c.z));
      el.innerHTML =
        '<img class="wph" src="' +
        CDN +
        "narrative-thumb-" +
        c.u +
        '.jpg" alt="" loading="lazy" decoding="async" draggable="false">' +
        '<span class="wdur">' +
        c.dur +
        "</span>" +
        '<span class="wmeta"><b>' +
        c.title +
        "</b><i>" +
        c.sub +
        "</i></span>";
      return el;
    }
    let half = 0;
    function build() {
      const cs = getComputedStyle(wall);
      const { tw, gap, cols } = wallMetrics(
        cs.getPropertyValue("--tw"),
        cs.getPropertyValue("--gap"),
        innerWidth,
      );
      const colH = tw * SHAPE["9/16"] + 2 * (tw * SHAPE["16/9"]) + 2 * gap;
      const swing = Math.max(
        0,
        Math.min(28, Math.floor((wall.clientHeight - colH) / 2)),
      );
      track.textContent = "";
      // Featured creators' videos take the columns under the middle of the
      // screen. The drift only starts once the wall is on screen, so this is
      // the arrangement people actually meet.
      const plan = planWallColumns(
        CLIPS,
        cols,
        wallCenterColumn(wall.clientWidth, tw, gap, cols),
      );
      const set = document.createDocumentFragment();
      plan.forEach((tiles, i) => {
        const col = document.createElement("div");
        col.className = "wcol";
        col.style.setProperty(
          "--off",
          (swing ? ((i * 37) % (swing * 2 + 1)) - swing : 0) + "px",
        );
        tiles.forEach((c) => col.appendChild(tile(c)));
        set.appendChild(col);
      });
      const setNode = document.createElement("div");
      setNode.style.cssText =
        "display:flex;align-items:center;gap:" + gap + "px;height:100%";
      setNode.appendChild(set);
      track.appendChild(setNode);
      track.appendChild(setNode.cloneNode(true));
      half = cols * (tw + gap);
    }

    const pin = $(".wall-pin");
    let drift = 0,
      lastT = 0,
      rafId = 0,
      running = false,
      onScreen = false;
    /* Sideways input. A trackpad's horizontal wheel or a finger/mouse dragging
       the wall writes straight into `drift`, so the wall follows the hand; a
       released swipe keeps coasting on `fling` and bleeds off over ~a third of
       a second. Both share the marquee's drift counter, so nothing jumps when
       the idle drift takes over again. Vertical gestures stay the page's:
       the wheel handler only claims a wheel that is mostly horizontal, and
       `touch-action:pan-y` lets the browser take vertical pans (it cancels our
       pointer when it does). */
    let fling = 0,
      dragging = false,
      dragMoved = false;
    const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const paused = () =>
      dragging ||
      wall.classList.contains("hold") ||
      wall.matches(":hover") ||
      popupOpen;
    function pinProgress() {
      if (!pin) return null;
      const r = pin.getBoundingClientRect();
      const travel = r.height - innerHeight;
      if (travel <= 40) return null;
      return Math.max(0, Math.min(1, -r.top / travel));
    }
    function frame(now: number) {
      const dt = lastT ? Math.min(64, now - lastT) : 0;
      lastT = now;
      if (!paused()) drift += (dt * SPEED) / 1000;
      if (fling) {
        drift += (fling * dt) / 1000;
        fling *= Math.exp(-dt / 320);
        if (Math.abs(fling) < 6) fling = 0;
      }
      if (half > 0) {
        const p = pinProgress();
        const raw = drift + (p === null ? 0 : p * half * 0.18);
        /* Swiping right takes drift negative; JS `%` keeps the sign, which
           would translate the track the wrong way and expose a bare edge. */
        const x = ((raw % half) + half) % half;
        track.style.transform = "translate3d(" + -x + "px,0,0)";
      }
      if (running) rafId = requestAnimationFrame(frame);
    }
    // The loop runs only while the wall is on screen and no popup is up:
    // nothing drifts unseen, and nothing competes with the popup's video.
    function startLoop() {
      if (running || !onScreen || popupOpen) return;
      running = true;
      lastT = 0;
      rafId = requestAnimationFrame(frame);
    }
    function stopLoop() {
      running = false;
      cancelAnimationFrame(rafId);
    }

    function mount(el: HTMLElement): HTMLVideoElement {
      let v = el.querySelector("video");
      if (v) return v;
      v = document.createElement("video");
      v.src = el.dataset.vid || "";
      v.loop = true;
      v.muted = true;
      v.playsInline = true;
      v.preload = "auto";
      el.insertBefore(v, el.querySelector(".wph")!.nextSibling);
      v.addEventListener("canplay", () => v!.classList.add("on"), {
        once: true,
      });
      return v;
    }
    function unmount(el: HTMLElement) {
      const v = el.querySelector("video");
      if (!v) return;
      v.pause();
      v.removeAttribute("src");
      v.load();
      v.remove();
    }
    function preview(el: HTMLElement) {
      const v = mount(el);
      const go = () => {
        const p = v.play();
        if (p) p.catch(() => {});
      };
      go();
      if (v.readyState < 3) v.addEventListener("canplay", go, { once: true });
    }
    function release(el: HTMLElement) {
      if (el === live) return;
      unmount(el);
    }

    /* ---- demo popup (rendered by React; see WallDemoPopup) ---- */
    function openClip(el: HTMLElement) {
      const c = CLIPS.find((x) => x.u === el.dataset.u);
      if (c) opts.onOpenClip(c);
    }
    popupHooks.push((open) => {
      if (open) {
        // The hovered tile's preview streams the same file the popup plays.
        track.querySelectorAll<HTMLElement>(".wtile").forEach(unmount);
        live = null;
        wall.classList.remove("hold");
        stopLoop();
      } else {
        startLoop();
      }
    });

    build();
    cleanups.push(stopLoop);
    if (fine) {
      on(track, "pointerover", (e) => {
        const el = (e.target as HTMLElement).closest<HTMLElement>(".wtile");
        if (el && !el.contains((e as PointerEvent).relatedTarget as Node))
          preview(el);
      });
      on(track, "pointerout", (e) => {
        const el = (e.target as HTMLElement).closest<HTMLElement>(".wtile");
        if (el && !el.contains((e as PointerEvent).relatedTarget as Node))
          release(el);
      });
    }
    on(track, "click", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(".wtile");
      if (el) openClip(el);
    });

    /* ---- swipe / horizontal wheel ---- */
    if (!reducedMotion) {
      on(
        wall,
        "wheel",
        (e) => {
          const w = e as WheelEvent;
          let dx = w.deltaX;
          if (Math.abs(dx) <= Math.abs(w.deltaY)) return;
          if (w.deltaMode === 1) dx *= 16;
          else if (w.deltaMode === 2) dx *= innerWidth;
          e.preventDefault();
          fling = 0;
          drift += dx;
        },
        { passive: false },
      );
      let pid = -1,
        px = 0,
        pt = 0,
        vx = 0;
      on(wall, "pointerdown", (e) => {
        const pe = e as PointerEvent;
        if (pe.pointerType === "mouse" && pe.button !== 0) return;
        dragging = true;
        dragMoved = false;
        fling = 0;
        pid = pe.pointerId;
        px = pe.clientX;
        pt = pe.timeStamp;
        vx = 0;
      });
      on(wall, "pointermove", (e) => {
        const pe = e as PointerEvent;
        if (!dragging || pe.pointerId !== pid) return;
        const dx = pe.clientX - px;
        if (!dragMoved) {
          if (Math.abs(dx) < 6) return;
          dragMoved = true;
          try {
            wall.setPointerCapture(pid);
          } catch {
            /* capture is a nicety; the drag still works without it */
          }
        }
        const dt = Math.max(1, pe.timeStamp - pt);
        vx = vx * 0.5 + (-dx / dt) * 1000 * 0.5;
        drift -= dx;
        px = pe.clientX;
        pt = pe.timeStamp;
      });
      const endDrag = (e: Event) => {
        const pe = e as PointerEvent;
        if (!dragging || pe.pointerId !== pid) return;
        dragging = false;
        if (dragMoved) {
          fling = Math.max(-2600, Math.min(2600, vx));
          /* The click that follows this pointerup must not open the popup;
             clear the flag once that click has had its turn. */
          setTimeout(() => {
            dragMoved = false;
          }, 0);
        }
      };
      on(wall, "pointerup", endDrag);
      on(wall, "pointercancel", endDrag);
      on(
        wall,
        "click",
        (e) => {
          if (!dragMoved) return;
          e.preventDefault();
          e.stopPropagation();
        },
        { capture: true },
      );
    }
    let seen = false;
    const wallIo = new IntersectionObserver(
      ([e]) => {
        onScreen = e.isIntersecting;
        if (onScreen) {
          if (!seen) {
            seen = true;
            opts.onWallSeen();
          }
          startLoop();
          return;
        }
        stopLoop();
        if (live) {
          live.classList.remove("is-live");
          unmount(live);
          live = null;
        }
        wall.classList.remove("hold");
      },
      { threshold: 0 },
    );
    wallIo.observe(wall);
    cleanups.push(() => wallIo.disconnect());
    let rz: ReturnType<typeof setTimeout>;
    on(window, "resize", () => {
      clearTimeout(rz);
      rz = setTimeout(() => {
        if (live) {
          live.classList.remove("is-live");
          live = null;
          wall.classList.remove("hold");
        }
        build();
      }, 180);
    });
    cleanups.push(() => clearTimeout(rz));
  });

  section("dock", pushDock);

  return {
    dispose: () => cleanups.forEach((fn) => fn()),
    setPopupOpen: (open) => {
      if (open === popupOpen) return;
      popupOpen = open;
      popupHooks.forEach((fn) => fn(open));
    },
    selectStyle,
  };
}

const tick = (
  <span className="tick">
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  </span>
);

const noteIcon = (stroke: string, size = 17) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const uploadIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v13" />
  </svg>
);

const diceIcon = (size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="8.5" cy="8.5" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="8.5" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="8.5" cy="15.5" r="1.35" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="15.5" r="1.35" fill="currentColor" stroke="none" />
  </svg>
);

const chevron = (cls?: string) => (
  <svg className={cls} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/* Store links mirror Landing.tsx's AppDownloadSection: same attribution,
   click ids and app_download_click event, so the dashboards keep working
   when this page takes over "/". */
function AppSection() {
  const { t } = useTranslation();
  const openStore = (platform: "ios" | "android") => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const attribution = {
      ...LANDING_ATTRIBUTION,
      clickId: createAppDownloadClickId(platform === "ios" ? "landing_ios" : "landing_android"),
    };
    const payload = { platform, action: "open", ...getAppDownloadTrackingParams(attribution) };
    window.dataLayer?.push({ event: "app_download_click", ...payload });
    window.gtag?.("event", "app_download_click", payload);
    if (platform === "ios") {
      if (getAppDownloadPlatform(navigator.userAgent) === "ios") {
        openIosAppStoreWithFallback(attribution);
        return;
      }
      window.open(getIosAppStoreUrl(attribution), "_blank", "noopener,noreferrer");
      return;
    }
    window.open(getAndroidPlayStoreUrl(attribution), "_blank", "noopener,noreferrer");
  };

  return (
    <section className="app" aria-labelledby="app-heading">
      <div className="sec-head" data-reveal="">
        <div className="l">
          <span className="mono">{t("landingV2.app.kicker")}</span>
          <h2 id="app-heading">{t("landingV2.app.h2")}</h2>
        </div>
      </div>
      <div className="app-frame" data-reveal="">
        <div className="app-copy">
          <p>{t("landingV2.app.p")}</p>
          <div className="app-stores">
            <a className="store" href={getIosAppStoreUrl(LANDING_ATTRIBUTION)} target="_blank" rel="noopener noreferrer" onClick={openStore("ios")}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.37 12.64c-.02-2.3 1.88-3.4 1.96-3.45-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.15-2.76.83-3.48.83-.72 0-1.83-.81-3-.79-1.55.02-2.97.9-3.77 2.28-1.61 2.79-.41 6.92 1.16 9.18.77 1.11 1.68 2.35 2.87 2.31 1.16-.05 1.59-.75 2.99-.75 1.39 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.24.88-1.29 1.24-2.53 1.26-2.6-.03-.01-2.42-.93-2.47-3.69zM14.09 5.9c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.78-.97 2.83 1.03.08 2.07-.52 2.71-1.29z" />
              </svg>
              <span>
                <small>{t("landingV2.app.storeSmall")}</small>
                <b>{t("landingV2.app.storeBig")}</b>
              </span>
            </a>
            <a className="store" href={getAndroidPlayStoreUrl(LANDING_ATTRIBUTION)} target="_blank" rel="noopener noreferrer" onClick={openStore("android")}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4.5 2.6a1.2 1.2 0 0 0-.5 1v16.8c0 .42.19.79.5 1l9.6-9.4-9.6-9.4zm11 8.1L6.4 3.4l11.2 6.4-2.1 0.9zm0 2.6l2.1.9L6.4 20.6l9.1-7.3zm1.9-1.5 2.9 1.66c.55.32.55.96 0 1.28l-2.9 1.66L14.9 12l2.5-.2z" />
              </svg>
              <span>
                <small>{t("landingV2.app.playSmall")}</small>
                <b>{t("landingV2.app.playBig")}</b>
              </span>
            </a>
          </div>
        </div>
        <div className="app-qr">
          <a className="qr" href={APP_DOWNLOAD_QR_TARGET_URL} aria-label={t("landingV2.app.qrAria")}>
            <img src={APP_DOWNLOAD_QR_IMAGE} alt={t("landingV2.app.qrAlt")} loading="lazy" />
          </a>
          <div>
            <b>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M14 14h3v3M21 14v3M14 21h3M21 21h.01" />
              </svg>
              {t("landingV2.app.scan")}
            </b>
            <p>{t("landingV2.app.qrNote")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const folderIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const assetsIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" />
  </svg>
);
const soundOffIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5L6 9H3v6h3l5 4z" />
    <path d="M16 9l5 6M21 9l-5 6" />
  </svg>
);
const soundOnIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5L6 9H3v6h3l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);

export default function LandingV2() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const [showAuth, setShowAuth] = useState(false);
  const { isAuthenticated } = useAuth();
  // Same title/description as newLanding.seo (en); the server injects the
  // crawler meta for "/" independently (server/_core/seo.ts).
  useSEO({
    title: t("newLanding.seo.title"),
    description: t("newLanding.seo.description"),
  });
  // Header Log in / Join go on to the creator; composer-triggered sign-in
  // reloads this page so the composer can restore its draft.
  const [authRedirect, setAuthRedirect] = useState<string | undefined>(CREATE_PATH);
  const [authTrigger, setAuthTrigger] = useState<AuthModalTrigger>("unknown");
  const [mode, setMode] = useState<ComposerV2Mode>("video");
  const [songLabel, setSongLabel] = useState<string | null>(null);
  const [dockSongOpen, setDockSongOpen] = useState(false);
  const composerRef = useRef<ComposerV2Handle>(null);
  // Which tile's demo is audible (slug), if any. Clips autoplay muted; the
  // speaker on a tile unmutes that one and mutes the rest.
  const [soundOn, setSoundOn] = useState<string | null>(null);
  const songAudioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLVideoElement>(".picker .clip").forEach((v) => {
      const on = v.dataset.slug === soundOn;
      v.muted = !on;
      if (on) {
        v.volume = 0.85;
        if (v.paused) void v.play().catch(() => setSoundOn(null));
      }
    });
    if (soundOn === "song") {
      let a = songAudioRef.current;
      if (!a) {
        a = new Audio(SONG_TILE_AUDIO);
        a.loop = true;
        a.volume = 0.85;
        songAudioRef.current = a;
      }
      void a.play().catch(() => setSoundOn(null));
    } else {
      songAudioRef.current?.pause();
    }
  }, [soundOn]);
  useEffect(() => () => songAudioRef.current?.pause(), []);
  const toggleSound = (slug: string) => setSoundOn((cur) => (cur === slug ? null : slug));

  // The gallery tile whose demo popup is open.
  const [activeClip, setActiveClip] = useState<WallClip | null>(null);
  const landingRef = useRef<LandingController | null>(null);
  useEffect(() => {
    landingRef.current?.setPopupOpen(!!activeClip);
    if (activeClip) setSoundOn(null);
  }, [activeClip]);
  const closeClip = useCallback(() => setActiveClip(null), []);
  const remixDemo = useCallback((demo: DemoMusicVideo) => {
    setActiveClip(null);
    landingRef.current?.selectStyle(0);
    composerRef.current?.loadDemo(demo);
  }, []);

  const requireAuthFromComposer = useCallback((trigger: AuthModalTrigger) => {
    setAuthRedirect(undefined);
    setAuthTrigger(trigger);
    setShowAuth(true);
  }, []);

  const goCreate = () => navigate(CREATE_PATH);

  useEffect(() => {
    // DM Sans / DM Mono, loaded once and left cached for the session.
    if (!document.querySelector("link[data-slv2-fonts]")) {
      const pre1 = document.createElement("link");
      pre1.rel = "preconnect";
      pre1.href = "https://fonts.googleapis.com";
      const pre2 = document.createElement("link");
      pre2.rel = "preconnect";
      pre2.href = "https://fonts.gstatic.com";
      pre2.crossOrigin = "anonymous";
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONTS_HREF;
      link.setAttribute("data-slv2-fonts", "");
      document.head.append(pre1, pre2, link);
    }
    const html = document.documentElement;
    const prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "smooth";

    let landing: LandingController | null = null;
    try {
      landing = rootRef.current
        ? initLanding(rootRef.current, {
            onPick: (i) => setMode(PICK_MODES[i] ?? "video"),
            onOpenClip: setActiveClip,
            onWallSeen: () => void loadWallDemoPopup().catch(() => {}),
            onSectionError: reportLandingSectionError,
            text: {
              captions: [1, 2, 3, 4, 5, 6].map((n) => t(`landingV2.hiw.c${n}`)),
              alts: [1, 2, 3, 4, 5, 6].map((n) => t(`landingV2.hiw.c${n}`)),
              step: (n) => t("landingV2.hiw.step", { n: String(n).padStart(2, "0") }),
              notPicked: t("landingV2.dock.notPicked"),
            },
          })
        : null;
    } catch (error) {
      // Outside the guarded sections (the picker/dock core): the page still
      // renders and links work, just without the scripted extras.
      reportLandingSectionError("init", error);
    }
    landingRef.current = landing;
    return () => {
      landing?.dispose();
      landingRef.current = null;
      html.style.scrollBehavior = prevScrollBehavior;
    };
  }, []);

  const styleCard = (
    accent: string,
    cls: string,
    ix: string,
    name: string,
    slug: string,
    hasClip: boolean,
    opts: { pressed?: boolean; letterbox?: boolean; sub?: string; badge?: string; title?: string } = {},
  ) => (
    <button className="style" aria-pressed={opts.pressed ? "true" : "false"} data-accent={accent} title={opts.title}>
      <div className={"art " + cls + (opts.letterbox ? " letterbox" : "")}>
        {opts.letterbox && (
          <img className="ph-bg" src={`/landing-v2/types/${slug}.webp?v=${TYPES_V}`} alt="" aria-hidden="true" decoding="async" />
        )}
        <img className="ph" src={`/landing-v2/types/${slug}.webp?v=${TYPES_V}`} alt={name} decoding="async" />
        {hasClip && (
          <video
            className="clip"
            src={`/landing-v2/types/${slug}.mp4?v=${TYPES_V}`}
            poster={`/landing-v2/types/${slug}.webp?v=${TYPES_V}`}
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
            data-slug={slug}
          />
        )}
        <span className="ix">{ix}</span>
        <span
          className="snd"
          role="button"
          tabIndex={0}
          aria-pressed={soundOn === slug}
          aria-label={soundOn === slug ? t("landingV2.picker.mute") : t("landingV2.picker.soundOn")}
          title={soundOn === slug ? t("landingV2.picker.mute") : t("landingV2.picker.soundOn")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleSound(slug);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              toggleSound(slug);
            }
          }}
        >
          {soundOn === slug ? soundOnIcon : soundOffIcon}
        </span>
        <span className="nm">
          {name}
          {opts.badge && <span className="badge">{opts.badge}</span>}
          {opts.sub && <span className="sub">{opts.sub}</span>}
        </span>
        {tick}
      </div>
    </button>
  );

  return (
    <div className="slv2" data-theme="dark" ref={rootRef}>
      <HeaderV2
        onLogin={() => { setAuthRedirect(CREATE_PATH); setAuthTrigger("header_sign_in"); setShowAuth(true); }}
        onJoin={() => { setAuthRedirect(CREATE_PATH); setAuthTrigger("header_join"); setShowAuth(true); }}
      />

      <main>
        <section className="hero">
          <div className="dome-rim" aria-hidden="true"></div>
          <div className="dome" aria-hidden="true"></div>
          <div className="disk-field" aria-hidden="true"></div>
          <div className="disk" aria-hidden="true">
            <div className="disk-enter">
              <div className="disk-spin">
                <img className="dl-outer-rainbow" src="/landing-v2/disk/outer-rainbow-ring.webp" alt="" />
                <img className="dl-outer-hollow" src="/landing-v2/disk/outer-ring-hollow.svg" alt="" />
                <img className="dl-teal-1" src="/landing-v2/disk/teal-gap-ring-1.svg" alt="" />
                <img className="dl-mid-rainbow" src="/landing-v2/disk/middle-rainbow-ring.webp" alt="" />
                <img className="dl-inner-rainbow" src="/landing-v2/disk/inner-rainbow-ring.svg" alt="" />
                <img className="dl-label-solid disk-upright" src="/landing-v2/disk/center-label-solid.svg" alt="" />
                <img className="dl-indicator disk-upright" src="/landing-v2/disk/center-play-indicator.svg" alt="" />
                <img className="dl-teal-2" src="/landing-v2/disk/teal-gap-ring-2.svg" alt="" />
                <img className="dl-groove-3" src="/landing-v2/disk/groove-line-3.svg" alt="" />
                <img className="dl-center-art disk-upright" src="/landing-v2/disk/center-art.webp" alt="" />
              </div>
            </div>
          </div>
          <div className="hero-in">
            <h1 data-enter="" style={{ "--d": "0ms" } as CSSProperties}>
              {t("landingV2.hero.h1")}
            </h1>
            <p className="sub" data-enter="" style={{ "--d": "80ms" } as CSSProperties}>
              {t("landingV2.hero.sub")}
            </p>

            {/* composer */}
            <ComposerV2
              ref={composerRef}
              mode={mode}
              onRequireAuth={requireAuthFromComposer}
              onSourceChange={setSongLabel}
              onDockSongOpenChange={setDockSongOpen}
            />

            {/* style first */}
            <div className="picker-frame" data-enter="" style={{ "--d": "200ms" } as CSSProperties}>
              <div className="picker-head">
                <span className="mono">{t("landingV2.picker.head")}</span>
              </div>
              <div className={"picker" + (SHOW_DANCE_CARD ? " scrolls" : "")} role="group" aria-label={t("landingV2.picker.head")}>
                {styleCard("#7B6FD6", "s1", "01", t("landingV2.picker.music"), "storyboard-video", true, { pressed: true, sub: t("landingV2.picker.musicSub") })}
                {styleCard("#D8434F", "s2", "02", t("landingV2.picker.singing"), "singing-video", true, { sub: t("landingV2.picker.singingSub") })}
                {SHOW_DANCE_CARD &&
                  styleCard("#22C55E", "s5", "03", t("landingV2.picker.dance", { defaultValue: "Dance video" }), "dance-video", true, {
                    sub: t("landingV2.picker.danceSub", { defaultValue: "Choreographed performance" }),
                    badge: t("landingV2.picker.beta", { defaultValue: "Beta" }),
                    title: t("landingV2.picker.danceBetaTip", {
                      defaultValue: "Full-song choreography planned section by section. The move library is still growing.",
                    }),
                  })}
                {styleCard("#19ADE6", "s3", SHOW_DANCE_CARD ? "04" : "03", t("landingV2.picker.lyric"), "lyric-video", true, { sub: t("landingV2.picker.lyricSub") })}
                {styleCard("#E68A19", "s4", SHOW_DANCE_CARD ? "05" : "04", t("landingV2.picker.song"), "song", false, { sub: t("landingV2.picker.songSub") })}
              </div>
            </div>

            {isAuthenticated && (
              <div className="mine-links" data-enter="" style={{ "--d": "240ms" } as CSSProperties}>
                <button type="button" className="chip" onClick={() => navigate("/projects")}>
                  {folderIcon} {t("landingV2.mine.projects")}
                </button>
                <button type="button" className="chip" onClick={() => navigate("/songs")}>
                  {assetsIcon} {t("landingV2.mine.assets")}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* gallery */}
        <section className="gallery">
          <div className="wall-pin">
            <div className="wall-stage">
              <div className="sec-head" data-reveal="">
                <div className="l">
                  <h2>{t("landingV2.gallery.h2")}</h2>
                </div>
                <div className="r">
                  <button className="browse" onClick={() => navigate("/featured")}>
                    {t("landingV2.gallery.browse")} <span className="n">1,240</span> →
                  </button>
                </div>
              </div>
              <div className="wall" id="wall" data-reveal="">
                <div className="wall-track" id="wallTrack"></div>
              </div>
            </div>
          </div>
        </section>

        <StatsBand />

        {/* how it works */}
        <section className="hiw">
          <div className="hiw-track">
            <div className="hiw-sticky">
              <div className="hiw-left">
                <div className="hiw-head" data-reveal="">
                  <h2>{t("landingV2.hiw.h2")}</h2>
                </div>
                <div className="beats">
                  <button className="beat" type="button" data-step="0" aria-pressed="true" data-reveal="" style={{ "--d": "0ms" } as CSSProperties}>
                    <div className="arc"></div>
                    <span className="n">01</span>
                    <span className="h">{t("landingV2.hiw.b1h")}</span>
                    <p>{t("landingV2.hiw.b1p")}</p>
                    <span className="det">{t("landingV2.hiw.b1d")}</span>
                  </button>
                  <button className="beat" type="button" data-step="1" aria-pressed="false" data-reveal="" style={{ "--d": "70ms" } as CSSProperties}>
                    <div className="arc"></div>
                    <span className="n">02</span>
                    <span className="h">{t("landingV2.hiw.b2h")}</span>
                    <p>{t("landingV2.hiw.b2p")}</p>
                    <span className="det">{t("landingV2.hiw.b2d")}</span>
                  </button>
                  <button className="beat" type="button" data-step="2" aria-pressed="false" data-reveal="" style={{ "--d": "140ms" } as CSSProperties}>
                    <div className="arc"></div>
                    <span className="n">03</span>
                    <span className="h">{t("landingV2.hiw.b3h")}</span>
                    <p>{t("landingV2.hiw.b3p")}</p>
                    <span className="det">{t("landingV2.hiw.b3d")}</span>
                  </button>
                  <button className="beat" type="button" data-step="3" aria-pressed="false" data-reveal="" style={{ "--d": "210ms" } as CSSProperties}>
                    <div className="arc"></div>
                    <span className="n">04</span>
                    <span className="h">{t("landingV2.hiw.b4h")}</span>
                    <p>{t("landingV2.hiw.b4p")}</p>
                    <span className="det">{t("landingV2.hiw.b4d")}</span>
                  </button>
                </div>
              </div>

              <div className="hiw-visual" data-reveal="">
                <div className="stage" id="hiwStage">
                  {/* display:contents on desktop; on stacked layouts it is the
                      picture box, with the tag + caption flowing beneath it */}
                  <div className="frame">
                  <img
                    className="shot"
                    id="hiwShot"
                    src="/product-tour/workflow-add-music.png"
                    alt={t("landingV2.hiw.c1")}
                    decoding="async"
                  />
                  <video id="hiwVideo" className="shot-video" playsInline controls preload="none" hidden></video>
                  <button type="button" className="play" id="hiwPlay" aria-label={t("landingV2.hiw.play")} hidden>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l14 8-14 8z" /></svg>
                  </button>
                  </div>
                  <span className="tag mono" id="hiwTag">
                    {t("landingV2.hiw.step", { n: "01" })}
                  </span>
                  <p id="hiwCaption">{t("landingV2.hiw.c1")}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* faq */}
        <section className="faq">
          <div className="sec-head" data-reveal="">
            <div className="l">
              <span className="mono">{t("landingV2.faq.kicker")}</span>
              <h2>{t("landingV2.faq.h2")}</h2>
            </div>
          </div>
          <div className="faq-list">
            <details data-reveal="" style={{ "--d": "0ms" } as CSSProperties}>
              <summary>
                <span className="q">{t("landingV2.faq.q1")}</span>
                <span className="pm"></span>
              </summary>
              <div className="a">{t("landingV2.faq.a1")}</div>
            </details>
            <details data-reveal="" style={{ "--d": "70ms" } as CSSProperties}>
              <summary>
                <span className="q">{t("landingV2.faq.q2")}</span>
                <span className="pm"></span>
              </summary>
              <div className="a">{t("landingV2.faq.a2")}</div>
            </details>
            <details data-reveal="" style={{ "--d": "140ms" } as CSSProperties}>
              <summary>
                <span className="q">{t("landingV2.faq.q3")}</span>
                <span className="pm"></span>
              </summary>
              <div className="a">{t("landingV2.faq.a3")}</div>
            </details>
            <details data-reveal="" style={{ "--d": "210ms" } as CSSProperties}>
              <summary>
                <span className="q">{t("landingV2.faq.q4")}</span>
                <span className="pm"></span>
              </summary>
              <div className="a">{t("landingV2.faq.a4")}</div>
            </details>
            <details data-reveal="" style={{ "--d": "280ms" } as CSSProperties}>
              <summary>
                <span className="q">{t("landingV2.faq.q5")}</span>
                <span className="pm"></span>
              </summary>
              <div className="a">{t("landingV2.faq.a5")}</div>
            </details>
            <details data-reveal="" style={{ "--d": "350ms" } as CSSProperties}>
              <summary>
                <span className="q">{t("landingV2.faq.q6")}</span>
                <span className="pm"></span>
              </summary>
              <div className="a">{t("landingV2.faq.a6")}</div>
            </details>
          </div>
        </section>

        <AppSection />

        {/* closing */}
        <section className="close">
          <div className="glow" aria-hidden="true"></div>
          <div className="record" aria-hidden="true"></div>
          <div className="veil" aria-hidden="true"></div>
          <div className="in" data-reveal="">
            <h2>
              {t("landingV2.close.h2a")}
              <br />
              {t("landingV2.close.h2b")}
            </h2>
            <p>{t("landingV2.close.p")}</p>
            <button className="cta" onClick={goCreate}>
              <span className="lbl">{t("landingV2.cta.music")}</span>
            </button>
          </div>
        </section>
        <div className="dome-foot" aria-hidden="true"></div>
      </main>

      {/* sticky composer — docks once the hero composer leaves, edits in place */}
      <div className="dock" id="dock" data-show="false" aria-hidden="true">
        <div className="dock-in">
          <span className="dock-bg" id="dockBg" aria-hidden="true"></span>

          <div className="dwrap look" id="lookWrap" data-open="false">
            <button className="dslot" id="lookBtn" aria-haspopup="true" aria-expanded="false">
              <span className="swatch" id="dockSwatch"></span>
              <span className="txt">
                <span className="k">{t("landingV2.dock.format")}</span>
                <span className="v empty" id="dockStyle">
                  {t("landingV2.dock.notPicked")}
                </span>
              </span>
              {chevron("chev")}
            </button>
            <div className="dpop looks" role="dialog" aria-label={t("landingV2.dock.pickFormat")}>
              <div className="lbl">{t("landingV2.dock.pickFormat")}</div>
              <div className="look-grid">
                {[
                  ["0", "#7B6FD6", "storyboard-video", t("landingV2.picker.music")],
                  ["1", "#D8434F", "singing-video", t("landingV2.picker.singing")],
                  ...(SHOW_DANCE_CARD
                    ? [["2", "#22C55E", "dance-video", t("landingV2.picker.dance", { defaultValue: "Dance video" })]]
                    : []),
                  [SHOW_DANCE_CARD ? "3" : "2", "#19ADE6", "lyric-video", t("landingV2.picker.lyric")],
                  [SHOW_DANCE_CARD ? "4" : "3", "#E68A19", "song", t("landingV2.picker.song")],
                ].map(([i, accent, slug, name]) => (
                  <button key={i} className="look-opt" data-i={i} data-accent={accent} aria-pressed="false">
                    <span className="fr">
                      <img src={`/landing-v2/types/${slug}.webp?v=${TYPES_V}`} alt="" loading="lazy" decoding="async" />
                    </span>
                    <span className="cap">{name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <span className="rule"></span>

          <div className="dwrap song" data-open={dockSongOpen}>
            <button
              className="dslot"
              id="songBtn"
              aria-haspopup="true"
              aria-expanded={dockSongOpen}
              onClick={() => composerRef.current?.toggleDockSongMenu()}
            >
              {noteIcon("rgba(16,17,16,.42)", 15)}
              <span className="txt">
                <span className="k">{t("landingV2.dock.song")}</span>
                <span className={"v" + (songLabel ? "" : " empty")} id="dockSong">
                  {songLabel ?? t("landingV2.dock.noSong")}
                </span>
              </span>
              {chevron("chev")}
            </button>
            <div className="dpop songs" id="dockSongPop" role="dialog" aria-label={t("landingV2.composer.selectSong")} onClick={(e) => e.stopPropagation()}></div>
          </div>

          <button className="cta" onClick={() => composerRef.current?.submit()}>
            <span className="lbl">
              <span className="long">{t("landingV2.cta.music")}</span>
              <span className="short">{t("landingV2.cta.short")}</span>
            </span>
          </button>
        </div>
      </div>

      <footer data-reveal="">
        <nav>
          <a
            href="/featured"
            onClick={(e) => {
              e.preventDefault();
              navigate("/featured");
            }}
          >
            {t("landingV2.footer.gallery")}
          </a>
          <a
            href="/subscription"
            onClick={(e) => {
              e.preventDefault();
              navigate("/subscription");
            }}
          >
            {t("landingV2.footer.pricing")}
          </a>
          <a
            href="/blog"
            onClick={(e) => {
              e.preventDefault();
              navigate("/blog");
            }}
          >
            {t("landingV2.footer.blog")}
          </a>
          <MusicGeneratorFooterLink />
          <AdGeneratorFooterLink />
          <BestMvFooterLink />
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noreferrer"
            title={t("landingV2.header.discordTitle", {
              defaultValue: "Join our Discord: share your videos and get featured",
            })}
            onClick={() => trackDiscordClick("landing_footer")}
          >
            {t("landingV2.footer.discord")}
          </a>
          <a href="/terms">{t("landingV2.footer.terms")}</a>
          <a href="/privacy">{t("landingV2.footer.privacy")}</a>
        </nav>
        <span className="mono">© 2026 Solmi</span>
        {/* Directories that list Solmi and ask for a crawlable link back
            (shared/listedOnDirectories.ts; the SSR body carries the same
            links for bots that don't run JS). */}
        <nav className="listed-on" aria-label={t("landingV2.footer.listedOn")}>
          <span className="mono">{t("landingV2.footer.listedOn")}</span>
          {LISTED_ON_DIRECTORIES.map((d) => (
            <a key={d.url} href={d.url} target="_blank" rel="noopener">
              {d.name}
            </a>
          ))}
        </nav>
      </footer>

      {activeClip && (
        <Suspense fallback={null}>
          <WallDemoPopup clipKey={activeClip.u} onClose={closeClip} onRemix={remixDemo} />
        </Suspense>
      )}

      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        redirectPath={authRedirect}
        trigger={authTrigger}
      />
    </div>
  );
}
